/* eslint-disable @typescript-eslint/no-explicit-any */
import { WarmupSpeed } from "@/hooks/use-mailbox";
import { AuthenticatedHttpClient } from "@/lib/axios";
import { AuthProvider } from "@/types/common";
import {
  AwsApiPayload,
  AwsSmtpPayload,
  AzureSmtpPayload,
  B2BWarmupSettings,
  ImapConfig,
  MailboxSettings,
  MailboxStatsResponse,
  MailboxType,
  SenderMailboxStats,
  SendGridApiPayload,
  SendGridSmtpPayload,
  SmtpConfig,
  SmtpValidationResponse,
  UpdateMailboxSettings,
  VerifySendGridApiPayload,
  VerifySendGridApiResponse,
  WarmupStats,
} from "@/types/mailbox.types";
import axios, { AxiosResponse } from "axios";

interface MailboxStats {
  connected: number;
  disconnected: number;
  warmupEnabled: number;
  warmupDisabled: number;
  totalDomains: number;
  coolDown: number;
  ready: number;
  warmingUp: number;
}

export class MailboxService {
  private http: AuthenticatedHttpClient;

  constructor(httpClient: AuthenticatedHttpClient) {
    this.http = httpClient;
  }

  async createMailbox<T>(
    mailboxData: Partial<MailboxType>
  ): Promise<AxiosResponse<T>> {
    return this.http.post<T>("/mailbox", mailboxData);
  }

  async getMailboxes(params: {
    page: number;
    limit: number;
    selectedLabels: string[];
    quickFilterText: string;
    selectedEspTypes?: string[];
    selectedWarmupStatus?: boolean | undefined;
    selectedPlanMetaData?: string[];
    selectedConnectionStatus?: boolean | undefined;
    minInboxPercentage?: number | undefined;
    maxInboxPercentage?: number | undefined;
  }) {
    // Explicitly include 0 values (not undefined) to ensure backend receives them
    const requestBody: any = {
      labels: params.selectedLabels,
      selectedEspTypes: params.selectedEspTypes || [],
      warmupEnabled: params.selectedWarmupStatus === true ? true : params.selectedWarmupStatus === false ? false : undefined,
      selectedPlanMetaData: params.selectedPlanMetaData || [],
      isActive: params.selectedConnectionStatus === true ? true : params.selectedConnectionStatus === false ? false : undefined,
    };
    
    // Explicitly set minCombinedScore and maxCombinedScore, including 0
    if (params.minInboxPercentage !== undefined) {
      requestBody.minCombinedScore = params.minInboxPercentage;
    }
    if (params.maxInboxPercentage !== undefined) {
      requestBody.maxCombinedScore = params.maxInboxPercentage;
    }
    
    console.log('MailboxService.getMailboxes - Request body:', JSON.stringify(requestBody, null, 2));
    console.log('MailboxService.getMailboxes - minInboxPercentage:', params.minInboxPercentage, 'maxInboxPercentage:', params.maxInboxPercentage);
    
    const response = await this.http.post<any>(
      `/mailbox/get-mailboxes?page=${params.page}&limit=${params.limit}&searchStr=${params.quickFilterText}`,
      requestBody
    );
    return response as unknown as { data: MailboxType[]; total: number };
  }

  async getDistinctEspTypes(): Promise<string[]> {
    const response = await this.http.get<any>('/mailbox/distinct-esp-types');
    return response as unknown as string[];
  }

  async searchMailboxes(email?: string, espType?: AuthProvider) {
    const url = email ? `/mailbox/search?email=${email}&espType=${espType}` : `/mailbox/search?espType=${espType}`;
    const response = await this.http.get(url);
    return response as unknown as any[];
  }

  async validateImap(account: ImapConfig) {
    return this.http.post(
      `/mailbox/verify-credentials/imap`,
      account
    ) as unknown as SmtpValidationResponse;
  }

  async validateSmtp(account: SmtpConfig) {
    return this.http.post(
      `/mailbox/verify-credentials/smtp`,
      account
    ) as unknown as SmtpValidationResponse;
  }
  async getMailboxesbyId(id: string) {
    const response = await this.http.get<any>(`/mailbox/${id}`);
    return response as unknown as MailboxType;
  }

  async getMailboxesbyEmail(email: string) {
    const response = await this.http.get<any>(`/mailbox/email/${email}`);
    return response as unknown as MailboxType;
  }

  async getMailboxStats() {
    const response = await this.http.get<any>("/mailbox/stats");
    return response as unknown as MailboxStats;
  }

  async updateMailbox<T>(
    id: string,
    mailboxData: Partial<MailboxType>
  ): Promise<AxiosResponse<T>> {
    return this.http.patch<T>(`/mailbox/${id}`, mailboxData);
  }

  async updateMailboxWarmup(
    id: string,
    mailboxData: Partial<MailboxType>
  ): Promise<MailboxType> {
    return this.http.patch(
      `/mailbox/${id}/warmup`,
      mailboxData
    ) as unknown as MailboxType;
  }

  /** Get B2B warmup settings for a mailbox (e.g. before enabling warmup) */
  async getB2BWarmupSettings(mailboxId: string): Promise<B2BWarmupSettings> {
    return this.http.get(
      `/mailbox/${mailboxId}/b2b-warmup-settings`
    ) as unknown as B2BWarmupSettings;
  }

  /** Save B2B warmup settings for a mailbox */
  async saveB2BWarmupSettings(
    mailboxId: string,
    settings: B2BWarmupSettings
  ): Promise<B2BWarmupSettings> {
    return this.http.patch(
      `/mailbox/${mailboxId}/b2b-warmup-settings`,
      settings
    ) as unknown as B2BWarmupSettings;
  }

  /** Enable warmup for one mailbox and store settings in one call */
  async enableWarmupWithSettings(
    mailboxId: string,
    settings: B2BWarmupSettings
  ): Promise<MailboxType> {
    return this.http.patch(
      `/mailbox/${mailboxId}/enable-warmup-settings`,
      settings
    ) as unknown as MailboxType;
  }

  /** Enable warmup for many mailboxes and store same settings in one call */
  async bulkEnableWarmupWithSettings(payload: {
    ids: string[];
  } & B2BWarmupSettings): Promise<{ updated?: number }> {
    return this.http.post(
      `/mailbox/bulk-enable-warmup-settings`,
      payload
    ) as unknown as { updated?: number };
  }

  async deleteMailbox<T>(id: string): Promise<AxiosResponse<T>> {
    return this.http.delete<T>(`/mailbox/${id}`);
  }

  async exportMailboxesAsCsv() {
    return this.http.get("/mailbox/export", {
      responseType: "blob",
    });
  }

  async importMailboxesFromCsv(file: File) {
    const formData = new FormData();
    formData.append("file", file);

    const response = await this.http.post("/mailbox/import", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response as unknown as { success: number; failed: number };
  }

  async bulkUpdateMailboxes<T>(payload: {
    ids: string[];
    updates: Partial<MailboxType>;
  }): Promise<AxiosResponse<T>> {
    return this.http.post<T>("/mailbox/bulk-update", payload);
  }

  async bulkDeleteMailboxes<T>(ids: string[]): Promise<AxiosResponse<T>> {
    return this.http.post<T>("/mailbox/bulk-delete", { ids });
  }

  async bulkUpdateMailboxWarmup<T>(payload: {
    ids: string[];
    updates: Partial<MailboxType>;
  }): Promise<AxiosResponse<T>> {
    return this.http.post<T>("/mailbox/bulk-warmup", payload);
  }

  async addLabel(id: string, labelId: string): Promise<AxiosResponse<any>> {
    return this.http.post(`/mailbox/${id}/labels`, { labelId });
  }

  async getSenderMailboxStats(
    id: string,
    startDate: string,
    endDate: string,
    espType?: string
  ) {
    let url = `/mailbox/sender-stats?id=${id}&startDate=${startDate}&endDate=${endDate}`;
    if (espType) {
      url += `&espType=${encodeURIComponent(espType)}`;
    }
    const response = await this.http.get(url);
    return response as unknown as SenderMailboxStats;
  }

  async getSenderEspTypes(email: string): Promise<string[]> {
    const response = await this.http.get<string[]>(
      `/mailbox/sender-esp-types/${encodeURIComponent(email)}`
    );
    return response as unknown as string[];
  }

  async removeLabel(id: string, labelId: string): Promise<AxiosResponse<any>> {
    return this.http.delete(`/mailbox/${id}/labels/${labelId}`);
  }

  async updateWarmupSpeed(id: string, updates: Partial<MailboxType>) {
    return this.http.patch(`/mailbox/${id}/warmup-speed`, updates);
  }

  async bulkUpdateLabels(
    ids: string[],
    labelIds: string[],
    operation: "add" | "remove"
  ): Promise<AxiosResponse<any>> {
    return this.http.post("/mailbox/bulk-labels", {
      ids,
      labelIds,
      operation,
    });
  }

  async getMailboxSettings(mailboxId: string) {
    return this.http.get(
      `/mailbox/${mailboxId}/template-settings`
    ) as unknown as MailboxSettings;
  }

  async updateMailboxSettings(
    mailboxId: string,
    settings: UpdateMailboxSettings
  ) {
    return this.http.patch(`/mailbox/${mailboxId}/template-settings`, settings);
  }

  async getActiveMailboxes() {
    const response = await this.http.get("/mailbox/fetch-active-mailboxes");
    return response as unknown as number;
  }

  async getActiveMailboxesDomain(searchTerm?: string, espType?: AuthProvider) {
    let url = `/mailbox/fetch-active-mailboxes-domain`;
    if (espType) {
      url = url + `?espType=${espType}`;
    }
    if (searchTerm) {
      url = url + `?domain=${searchTerm}`;
    }
    const response = await this.http.get(`${url}`);
    return response as unknown as { domain: string; mailboxId: string }[];
  }

  async getUniqueDomains(authProvider: AuthProvider) {
    const response = await this.http.get(`/mailbox/unique-domains?authProvider=${authProvider}`);
    return response as unknown as string[];
  }

  async getActiveMailboxesAsCsv() {
    return this.http.get("/mailbox/active-export", {
      responseType: "blob",
    });
  }

  async getSeedSenders() {
    return this.http.get("/warmup");
  }

  async createSeedSender(emails: string[]) {
    // Legacy method - only sends emails
    return this.http.post("/warmup", { emails: emails });
  }

  async createB2cSenders(senders: Array<{ email: string; firstName?: string; lastName?: string; espType?: string }>) {
    // Use new B2C Sender API - sends full sender data including ESP type
    console.log('createB2cSenders called with:', senders);
    try {
      const response = await this.http.post<any>("/b2c-sender/bulk", { senders });
      console.log('createB2cSenders response:', response);
      return response as unknown as any;
    } catch (error) {
      // Fallback to old endpoint if new one doesn't exist
      console.log('B2C Sender endpoint not available, falling back to /warmup');
      const emails = senders.map(s => s.email);
      return this.http.post("/warmup", { emails });
    }
  }

  // ------------------- Warmup Accounts / B2C Senders -------------------

  async getWarmupAccounts(params?: {
    page?: number;
    limit?: number;
    labels?: string[];
    subscriptionIds?: string[];
    planIds?: string[];
    connectionStatus?: string;
    minSendingVolume?: number;
    maxSendingVolume?: number;
    minSeedVolume?: number;
    maxSeedVolume?: number;
    minSeedAssign?: number;
    maxSeedAssign?: number;
  }) {
    // Use new B2C Sender API endpoint with GET as per API docs
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.labels && params.labels.length > 0) queryParams.append('labels', params.labels.join(','));
    if (params?.subscriptionIds && params.subscriptionIds.length > 0) queryParams.append('subscriptionIds', params.subscriptionIds.join(','));
    if (params?.planIds && params.planIds.length > 0) queryParams.append('planIds', params.planIds.join(','));
    if (params?.connectionStatus) queryParams.append('connectionStatus', params.connectionStatus);
    if (params?.minSendingVolume !== undefined) queryParams.append('minSendingVolume', params.minSendingVolume.toString());
    if (params?.maxSendingVolume !== undefined) queryParams.append('maxSendingVolume', params.maxSendingVolume.toString());
    if (params?.minSeedVolume !== undefined) queryParams.append('minSeedVolume', params.minSeedVolume.toString());
    if (params?.maxSeedVolume !== undefined) queryParams.append('maxSeedVolume', params.maxSeedVolume.toString());
    if (params?.minSeedAssign !== undefined) queryParams.append('minSeedAssign', params.minSeedAssign.toString());
    if (params?.maxSeedAssign !== undefined) queryParams.append('maxSeedAssign', params.maxSeedAssign.toString());

    try {
      // Try new B2C Sender API endpoint first
      const response = await this.http.get<any>(`/b2c-sender?${queryParams.toString()}`);
      
      // Handle response - it might be the array directly or wrapped in { data, meta }
      if (Array.isArray(response)) {
        return { data: response, meta: {} };
      }
      
      // If response has data property, return as is
      if (response?.data && Array.isArray(response.data)) {
        return response;
      }
      
      // If response structure is different, try to extract data
      console.warn('Unexpected response format from /b2c-sender:', response);
      return { data: [], meta: {} };
    } catch (error) {
      // Fallback to old endpoint if new one doesn't exist
      console.log('B2C Sender endpoint not available, falling back to /warmup');
      const response = await this.http.get<any>("/warmup");
      return { data: Array.isArray(response) ? response : [], meta: {} };
    }
  }

  // B2C Sender CRUD operations
  async createB2cSender(senderData: any) {
    const response = await this.http.post<any>("/b2c-sender", senderData);
    return response as unknown as any;
  }

  async createBulkB2cSenders(senders: any[]) {
    const response = await this.http.post<any>("/b2c-sender/bulk", { senders });
    return response as unknown as any;
  }

  async getB2cSenderById(id: string) {
    const response = await this.http.get<any>(`/b2c-sender/${id}`);
    return response as unknown as any;
  }

  async updateB2cSender(id: string, updates: any) {
    const response = await this.http.patch<any>(`/b2c-sender/${id}`, updates);
    return response as unknown as any;
  }

  async bulkUpdateB2cSenders(ids: string[], updates: any) {
    const response = await this.http.patch<any>("/b2c-sender/bulk", { ids, updates });
    return response as unknown as any;
  }

  async toggleWarmupAccount(id: string, enabled: boolean, email: string) {
    // Map to new API structure
    return this.http.patch(`/b2c-sender/${id}`, {
      isWarmupEnabled: enabled,
      email: email,
    });
  }

  async bulkToggleWarmupAccounts(ids: string[], enabled: boolean) {
    // Use new bulk update endpoint
    return this.http.patch("/b2c-sender/bulk", {
      ids,
      updates: { isWarmupEnabled: enabled },
    });
  }

  async deleteWarmupAccount(id: string) {
    return this.http.delete(`/b2c-sender/${id}`);
  }

  async bulkDeleteWarmupAccounts(ids: string[]) {
    // For now, delete individually or update if backend adds bulk delete
    // You may need to implement batch deletion
    return this.http.post("/b2c-sender/bulk-delete", { ids });
  }

  async getWarmupStats(): Promise<WarmupStats[]> {
    const response = await this.http.get("/warmup/stats");
    return response as unknown as WarmupStats[];
  }

  async fetchSenderInteractions(params: {
    email: string;
    page?: string;
    limit?: string;
    isExport?: string;
    exportType?: string;
  }) {
    const response = await this.http.post(
      "/mailbox/sender-interactions",
      {
        email: params.email,
        page: params.page,
        limit: params.limit,
        isExport: params.isExport,
        exportType: params.exportType,
      },
      {
        responseType: params.isExport ? "blob" : "json",
      }
    );
    return response as unknown as any;
  }

  async getMailboxDailyStats(
    id: string,
    startDate: string,
    endDate: string,
    isMailboxStats: boolean,
    espType?: string
  ) {
    let url = `/mailbox/${id}/daily-stats?startDate=${startDate}&endDate=${endDate}&isMailboxStats=${isMailboxStats}`;
    if (espType) {
      url += `&espType=${encodeURIComponent(espType)}`;
    }
    const response = await this.http.get(url);
    return response as unknown as MailboxStatsResponse;
  }

  async runBulkEmailTest(domains: string[], espType: AuthProvider) {
    return await this.http.post("/mailbox/bulk-test", { domains, espType });
  }

  async googleOauthInit() {
    return this.http.post("/mailbox/google/oauth-init");
  }

  async googleOauthCallback(code: string) {
    return this.http.post("/mailbox/google/oauth-callback", { code });
  }

  async microsoftOauthInit() {
    return this.http.post("/mailbox/microsoft/oauth-init");
  }

  async microsoftOauthCallback(code: string) {
    return this.http.post("/mailbox/microsoft/oauth-callback", { code });
  }

  async googleWorkspaceImport(data: {
    customerId: string;
    adminEmail: string;
  }): Promise<{
    success: number;
    skipped: number;
    failed: number;
  }> {
    const response = await this.http.post(
      "/mailbox/google/workspace-import",
      data
    );
    return response as unknown as {
      success: number;
      skipped: number;
      failed: number;
    };
  }

  async googleWorkspaceAvailableMailboxes(data: {
    customerId: string;
    adminEmail: string;
  }): Promise<{
    users: Array<{
      email: string;
      firstName: string;
      lastName: string;
    }>;
  }> {
    const response = await this.http.post(
      "/mailbox/google/workspace-available-mailboxes",
      data
    );
    return response as unknown as {
      users: Array<{ email: string; firstName: string; lastName: string }>;
    };
  }

  async googleWorkspaceImportSelected(data: {
    customerId: string;
    adminEmail: string;
    emails: string[];
  }): Promise<{ added: Array<{ email: string; mailboxId: string }> }> {
    const response = await this.http.post(
      "/mailbox/google/workspace-import-selected",
      data
    );
    return response as unknown as {
      added: Array<{ email: string; mailboxId: string }>;
    };
  }

  async addAwsApi(data: AwsApiPayload) {
    return this.http.post("/mailbox/aws/api", data);
  }

  async addAwsSmtp(data: AwsSmtpPayload) {
    return this.http.post("/mailbox/aws/smtp", data);
  }

  async addSendGridApi(data: SendGridApiPayload) {
    return this.http.post("/mailbox/sendgrid/api", data);
  }

  async verifySendGridApi(data: VerifySendGridApiPayload) {
    const response = await this.http.post("/mailbox/sendgrid/api/verify", data);
    return response as unknown as VerifySendGridApiResponse;
  }

  async addSendGridSmtp(data: SendGridSmtpPayload) {
    return this.http.post("/mailbox/sendgrid/smtp", data);
  }

  async addAzureSmtp(data: AzureSmtpPayload) {
    return this.http.post("/mailbox/azure", data);
  }

  async addGoogleWorkspaceSmtp(data: Partial<MailboxType>) {
    return this.http.post("/mailbox/google-workspace-smtp", data);
  }

  // Seed counts endpoint
  async getSeedCounts() {
    const response = await this.http.get<any>("/b2c-sender/seed-counts");
    return response as unknown as SeedCountData[];
  }

  // Download seeds by ESP type
  async downloadSeeds(id: string, espType: string) {
    return this.http.get<any>(`/b2c-sender/${id}/download-seeds/${espType}`, {
      responseType: "blob",
    });
  }

  // Download replacements by ESP type
  async downloadReplacements(id: string, espType: string) {
    return this.http.get<any>(`/b2c-sender/${id}/download-replacements/${espType}`, {
      responseType: "blob",
    });
  }

  // Download all seeds
  async downloadAllSeeds(id: string) {
    return this.http.get<any>(`/b2c-sender/${id}/download-all-seeds`, {
      responseType: "blob",
    });
  }

  // Download all replacements
  async downloadAllReplacements(id: string) {
    return this.http.get<any>(`/b2c-sender/${id}/download-all-replacements`, {
      responseType: "blob",
    });
  }

  // Fetch hostnames for sender IPs
  async fetchSenderIpHostnames(data: {
    sender_email: string;
    esp_type: string;
    date: string;
    ips: string[];
  }) {
    return this.http.post<any>("/mailbox/sender-ips/fetch-hostnames", data);
  }

  // Download failed headers for a specific sender
  async downloadFailedHeader(date: string, email: string) {
    return this.http.get<Blob>(
      "/mailbox/download-failed-headers",
      { date, senderEmail:email },
      "blob"
    );
  }
}

export interface SeedCountData {
  id: string;
  email: string;
  senderEspType: string;
  seedCounts: {
    gmail: number;
    outlook: number;
    yahoo: number;
    "Google-Workspace": number;
    Microsoft365: number;
  };
  replacementAvailability: {
      gmail: boolean,
      outlook: boolean,
      yahoo: boolean,
      "Google-Workspace": boolean,
      Microsoft365: boolean,
    };
  seedEspTypes: string;
  lastSeedUpdate: string;
}
